import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Radio,
  RadioItem,
  Select,
  MenuItem,
  Label,
  Button,
  Modal,
  ModalButtons,
  ModalContent,
  Divider,
  ProgressBar,
  withForm,
  Utils,
} from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import { SHOW_NOTIFY, NOTIFY_LEVEL } from 'constants';
import {
  CREATE_ACTORS_ACCOUNT,
  UPDATE_ACTORS_ACCOUNT,
  BULK_CREATE_ACCOUNTS,
} from '@control-front-end/common/constants/actorAccounts';
import Toggle from '@control-front-end/common/components/Toggle';
import AccAdvancedOptions from '@control-front-end/common/components/AccAdvancedOptions';
import useIntl from 'useIntl';
import mes from './intl';
// eslint-disable-next-line no-unused-vars
import m from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import l from './CreateActorAccount.scss'; // NOSONAR

function CreateActorAccountBase(props) {
  const {
    values,
    handleSubmit,
    handleOnChange,
    visibility,
    onClose,
    isSubmit,
    errors,
    data: { accounts = [], accNames, currencies, mode = 'create' },
  } = props;
  const t = useIntl();

  const renderAccNames = () => {
    const items = accNames.map((i) => (
      <MenuItem key={i.id.toString()} label={i.name} value={i.id.toString()} />
    ));
    if (!items.length) {
      return <MenuItem label={t(mes.currenciesNotFound)} />;
    }
    return items;
  };

  const renderCurrencies = () => {
    const list =
      values.actionName === 'select' && !values.nameId ? [] : currencies;
    const excluded =
      values.actionName === 'select'
        ? accounts
            .filter((acc) => acc.nameId === values.nameId)
            .map((i) => i.currencyId.toString())
        : [];
    const items = list.map((i) => (
      <MenuItem
        key={i.id.toString()}
        label={i.name}
        value={i.id.toString()}
        visibility={excluded.includes(i.id.toString()) ? 'disabled' : 'visible'}
      />
    ));
    if (!items.length) {
      return <MenuItem label={t(mes.currenciesNotFound)} />;
    }
    return items;
  };

  const renderActionRadio = (type) => (
    <Radio
      id={`action${Utils.toUpperLatter(type)}`}
      align="horizontal"
      value={values[`action${Utils.toUpperLatter(type)}`]}
      onChange={handleOnChange}
    >
      <RadioItem
        label={t(mes[`selectAcc${Utils.toUpperLatter(type)}`])}
        value="select"
      />
      <RadioItem
        label={t(mes[`createAcc${Utils.toUpperLatter(type)}`])}
        value="create"
      />
    </Radio>
  );

  const renderTextField = (field) => {
    const { id, label } = field;
    return (
      <TextField
        id={id}
        styleName="m.textfield l.field"
        bordered={true}
        value={values[id] || ''}
        error={!!errors[id]}
        placeholder={label}
        helperText={t(mes[errors[id]])}
        onChange={handleOnChange}
      />
    );
  };
  return (
    <Modal
      styleName="m.modal"
      onClose={onClose}
      label={t(mes.createNewAccount)}
      visibility={visibility}
    >
      <ModalContent styleName="m.modal__content l.content">
        {mode === 'create' ? (
          <>
            <div styleName="l.block">
              <div styleName="l.block__label">
                <Label fontWeight="semibold" value={t(mes.account)} />
              </div>
              {renderActionRadio('name')}
              {values.actionName === 'create' ? (
                renderTextField({ id: 'accName', label: t(mes.enterAccName) })
              ) : (
                <Select
                  id="nameId"
                  styleName="m.select l.field"
                  type="autocomplete"
                  bordered={true}
                  value={
                    values.nameId ? values.nameId.toString() : values.nameId
                  }
                  error={!!errors.nameId}
                  helperText={t(mes[errors.nameId])}
                  onChange={handleOnChange}
                  portalOptions={{ offsetY: 66, offsetX: 4 }}
                >
                  {renderAccNames()}
                </Select>
              )}
            </div>
            <div styleName="l.block">
              <div styleName="l.block__label">
                <Label fontWeight="semibold" value={t(mes.currency)} />
              </div>
              {renderActionRadio('currency')}
              {values.actionCurrency === 'create' ? (
                renderTextField({
                  id: 'currencyName',
                  label: t(mes.enterAccCurrencyName),
                })
              ) : (
                <Select
                  id="currencyId"
                  styleName="m.select l.field"
                  type="autocomplete"
                  bordered={true}
                  value={
                    values.currencyId
                      ? values.currencyId.toString()
                      : values.currencyId
                  }
                  error={!!errors.currencyId}
                  helperText={t(mes[errors.currencyId])}
                  onChange={handleOnChange}
                  visibility={
                    values.actionName === 'select' && !values.nameId
                      ? 'disabled'
                      : 'visible'
                  }
                  portalOptions={{ offsetY: 66, offsetX: 4 }}
                >
                  {renderCurrencies()}
                </Select>
              )}
            </div>
            <Divider styleName="l.divider" />
          </>
        ) : null}
        <Toggle
          title={t(mes.advancedOptions)}
          theme="white"
          isOpen={mode === 'edit'}
        >
          <AccAdvancedOptions
            values={values}
            errors={errors}
            mode={mode}
            addPlan={mode === 'create'}
            handleOnChange={handleOnChange}
          />
        </Toggle>
      </ModalContent>
      <ModalButtons styleName="m.modal__buttons">
        <Button
          label={t(mes.save)}
          size="large"
          onClick={handleSubmit}
          visibility={isSubmit ? 'disabled' : 'visible'}
        />
        <ProgressBar
          styleName="m.modal__loader"
          type="circle"
          size="small"
          visibility={isSubmit ? 'visible' : 'hidden'}
        />
      </ModalButtons>
    </Modal>
  );
}

CreateActorAccountBase.propTypes = {
  visibility: PropTypes.bool,
  onClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleOnChange: PropTypes.func,
  isSubmit: PropTypes.bool,
  values: PropTypes.object,
  errors: PropTypes.object,
  data: PropTypes.object,
};

const CreateActorAccount = withForm(
  {
    mapPropsToValues(props) {
      const currencies = props.data.currencies || [];
      const accData = props.data.accData || {};
      return {
        key: accData.key,
        treeCalculation: AppUtils.isUndefined(accData.treeCalculation)
          ? true
          : accData.treeCalculation,
        search: AppUtils.isUndefined(accData.search) ? true : accData.search,
        minLimit: accData.minLimit,
        maxLimit: accData.maxLimit,
        nameId: accData.nameId || '',
        currencyId: accData.currencyId || '',
        currencyName: '',
        accName: '',
        accountType: 'fact',
        addPlan: false,
        actionCurrency: currencies.length ? 'select' : 'create',
        actionName: 'select',
      };
    },
    validate: ({ actionCurrency, currencyName, currencyId, mode }) => {
      if (mode === 'create' && actionCurrency === 'create' && !currencyName) {
        return { currencyName: 'field_required' };
      }
      if (mode === 'create' && actionCurrency === 'select' && !currencyId) {
        return { currencyId: 'field_required' };
      }
      return {};
    },
    handleSubmit: (values, dispatch, formActions, props) => {
      const { actorId, actors, mode } = props.data;

      const notifySuccess = () => {
        const text = `${
          actors || values.addPlan ? 'Accounts' : 'Account'
        } successfully created`;
        dispatch({
          type: SHOW_NOTIFY.REQUEST,
          payload: {
            id: AppUtils.createRid(),
            type: NOTIFY_LEVEL.SUCCESS,
            label: text,
          },
        });
      };

      const createAccounts = ({ type, target }) => {
        dispatch({
          type,
          payload: { ...target, ...values },
          callback: (data) => {
            if (!values.addPlan) {
              notifySuccess();
              return;
            }
            const [factAcc] = data;
            const { currencyId, nameId } = factAcc;
            dispatch({
              type,
              payload: { accountType: 'plan', currencyId, nameId, ...target },
              callback: () => notifySuccess(),
            });
          },
        });
        props.onClose();
      };

      if (actors) {
        createAccounts({
          type: BULK_CREATE_ACCOUNTS.REQUEST,
          target: { actors },
        });
      } else if (actorId) {
        createAccounts({
          type:
            mode === 'create'
              ? CREATE_ACTORS_ACCOUNT.REQUEST
              : UPDATE_ACTORS_ACCOUNT.REQUEST,
          target: { actorId },
        });
      }
    },
  },
  CreateActorAccountBase
);

export default CreateActorAccount;
